import { useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, theme, Badge, Drawer } from 'antd';
import {
  PictureOutlined,
  ShopOutlined,
  NotificationOutlined,
  InboxOutlined,
  SolutionOutlined,
  CommentOutlined,
  CalendarOutlined,
  TeamOutlined,
  InstagramOutlined,
  FundOutlined,
  FormOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  FileTextOutlined,
  ReadOutlined,
  PhoneOutlined,
  MailOutlined,
  CrownOutlined,
  MenuOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  EditOutlined,
  ToolOutlined,
  GlobalOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import API from '../api/client';
import './AdminLayout.css';

const { Header, Sider, Content } = Layout;

const superAdminMenuItems = [
  {
    key: 'content',
    icon: <EditOutlined />,
    label: 'İçerik Yönetimi',
    children: [
      { key: '/admin/slider', icon: <PictureOutlined />, label: 'Slider' },
      { key: '/admin/about', icon: <DashboardOutlined />, label: 'Hakkımızda' },
      { key: '/admin/announcements', icon: <NotificationOutlined />, label: 'Duyurular' },
      { key: '/admin/events', icon: <CalendarOutlined />, label: 'Etkinlikler' },
      { key: '/admin/pages', icon: <ReadOutlined />, label: 'Sayfalar (KVKK)' },
    ],
  },
  {
    key: 'business',
    icon: <ShopOutlined />,
    label: 'İşletmeler',
    children: [
      { key: '/admin/businesses', icon: <AppstoreOutlined />, label: 'Tüm İşletmeler' },
    ],
  },
  {
    key: 'marketing',
    icon: <FundOutlined />,
    label: 'Pazarlama',
    children: [
      { key: '/admin/banners', icon: <FundOutlined />, label: 'Reklam Alanları' },
      { key: '/admin/partners', icon: <TeamOutlined />, label: 'Markalar' },
      { key: '/admin/instagram', icon: <InstagramOutlined />, label: 'Instagram' },
      { key: '/admin/cta', icon: <FormOutlined />, label: 'QR & Başvuru' },
    ],
  },
  {
    key: 'communication',
    icon: <MailOutlined />,
    label: 'İletişim',
    children: [
      { key: '/admin/contact', icon: <PhoneOutlined />, label: 'İletişim Sayfası' },
      { key: '/admin/messages', icon: <MailOutlined />, label: 'Gelen Mesajlar' },
      { key: '/admin/supplier-applications', icon: <InboxOutlined />, label: 'Tedarikçi Başvuruları' },
      { key: '/admin/job-applications', icon: <SolutionOutlined />, label: 'İş Başvuruları' },
      { key: '/admin/feedbacks', icon: <CommentOutlined />, label: 'Görüşler' },
    ],
  },
  {
    key: 'site',
    icon: <ToolOutlined />,
    label: 'Site Ayarları',
    children: [
      { key: '/admin/settings', icon: <GlobalOutlined />, label: 'SEO & Logo' },
      { key: '/admin/footer', icon: <FileTextOutlined />, label: 'Footer' },
    ],
  },
  { type: 'divider' },
  { key: '/admin/users', icon: <CrownOutlined />, label: 'Kullanıcılar' },
];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const AdminLayout = () => {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [restaurantMenuItems, setRestaurantMenuItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin } = useAuth();
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  useEffect(() => {
    if (!isSuperAdmin && user) {
      API.get('/my-businesses').then(res => {
        const items = res.data.map(biz => ({
          key: `biz-${biz._id}`,
          icon: <ShopOutlined />,
          label: biz.name,
          children: [
            { key: `/admin/menu/${biz._id}`, icon: <MenuOutlined />, label: 'Menü Düzenle' },
            { key: `/admin/qr-generator/${biz._id}`, icon: <FormOutlined />, label: 'QR Kod' },
          ],
        }));
        setRestaurantMenuItems(items);
      }).catch(() => {});
    }
  }, [isSuperAdmin, user]);

  useEffect(() => {
    if (isSuperAdmin) {
      API.get('/contact-messages/unread-count')
        .then(res => setUnreadCount(res.data.count || 0))
        .catch(() => {});
    }
  }, [isSuperAdmin, location.pathname]);

  const menuItems = isSuperAdmin ? superAdminMenuItems : restaurantMenuItems;

  const getOpenKeys = () => {
    const path = location.pathname;
    for (const item of superAdminMenuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.key === path) return [item.key];
        }
      }
    }
    return [];
  };

  const [openKeys, setOpenKeys] = useState(getOpenKeys());

  useEffect(() => {
    const keys = getOpenKeys();
    if (keys.length > 0 && !openKeys.includes(keys[0])) {
      setOpenKeys(prev => [...new Set([...prev, ...keys])]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close drawer on navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const userMenu = {
    items: [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Çıkış Yap',
        danger: true,
        onClick: () => {
          logout();
          navigate('/login');
        },
      },
    ],
  };

  const handleMenuClick = ({ key }) => {
    if (!key.startsWith('biz-') && !['content','business','marketing','communication','site'].includes(key)) {
      navigate(key);
    }
  };

  const sidebarContent = (
    <>
      <div style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'space-between' : 'center',
        padding: isMobile ? '0 16px' : 0,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #D4A843, #f0c96b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 16, color: '#1E1659',
          }}>D</div>
          {(!collapsed || isMobile) && (
            <span style={{ color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: 0.5 }}>
              Dihiz <span style={{ color: '#D4A843', fontWeight: 400, fontSize: 13 }}>Panel</span>
            </span>
          )}
        </div>
        {isMobile && (
          <Button type="text" icon={<CloseOutlined />} onClick={() => setDrawerOpen(false)}
            style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16 }} />
        )}
      </div>
      <div style={{ padding: '8px 0', flex: 1, overflow: 'auto' }}>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={collapsed && !isMobile ? [] : openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ background: 'transparent', borderRight: 'none' }}
          theme="dark"
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={250}
          className="admin-sidebar"
          style={{
            background: 'linear-gradient(180deg, #1a1145 0%, #1E1659 40%, #251b6e 100%)',
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0, top: 0, bottom: 0,
            zIndex: 100,
            boxShadow: '2px 0 12px rgba(0,0,0,0.15)',
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={280}
          closable={false}
          styles={{
            body: { padding: 0, background: 'linear-gradient(180deg, #1a1145 0%, #1E1659 40%, #251b6e 100%)' },
            header: { display: 'none' },
          }}
          className="admin-mobile-drawer"
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout style={{
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 250),
        transition: 'margin-left 0.2s',
      }}>
        <Header style={{
          padding: '0 16px',
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          height: 56,
        }}>
          <Button
            type="text"
            icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed)}
            style={{ fontSize: 16, width: 40, height: 40 }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isSuperAdmin && unreadCount > 0 && (
              <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                <Button type="text" icon={<MailOutlined />}
                  onClick={() => navigate('/admin/messages')} style={{ fontSize: 16 }} />
              </Badge>
            )}
            <Dropdown menu={userMenu} placement="bottomRight">
              <div style={{
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 8px', borderRadius: 8, transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Avatar icon={<UserOutlined />}
                  style={{
                    background: isSuperAdmin ? 'linear-gradient(135deg, #D4A843, #f0c96b)' : '#2F2386',
                    color: isSuperAdmin ? '#1E1659' : '#fff',
                  }}
                  size={32}
                />
                {!isMobile && (
                  <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{user?.displayName || user?.username}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {isSuperAdmin ? 'Süper Admin' : 'Restoran Yetkilisi'}
                    </div>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{
          margin: isMobile ? 8 : 20,
          padding: isMobile ? 12 : 24,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          minHeight: 'calc(100vh - 80px)',
          overflow: 'auto',
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
